import React, { useState, useRef } from 'react';
import {
  UploadCloud,
  FileAudio,
  CheckCircle2,
  AlertCircle,
  Trash2,
  ArrowRight,
  ArrowLeft,
  Sliders,
  Sparkles,
  RefreshCw,
  MoveUp,
  MoveDown,
} from 'lucide-react';
import { convertAudioToWav, SUPPORTED_AUDIO_EXTENSIONS } from '../../lib/audioProcessor';
import { validateTrackCount } from '../../lib/distributionService';

export default function Step2AudioUpload({
  releaseType,
  audioTracks,
  onUpdateAudioTracks,
  bitDepth,
  onChangeBitDepth,
  onContinue,
  onBack,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [processingMap, setProcessingMap] = useState({}); // { [fileId]: { progress, stage, message, error } }
  const fileInputRef = useRef(null);

  const isSingle = releaseType === 'single';

  const handleFiles = async (filesList) => {
    const incomingFiles = Array.from(filesList);
    if (incomingFiles.length === 0) return;

    // For single, take only the first file
    const targetFiles = isSingle ? incomingFiles.slice(0, 1) : incomingFiles;

    // Check if adding exceeds single limit
    if (isSingle && audioTracks.length >= 1) {
      // Replace existing single track
      onUpdateAudioTracks([]);
    }

    for (let i = 0; i < targetFiles.length; i++) {
      const file = targetFiles[i];
      const trackId = `track_${Date.now()}_${i}_${Math.random().toString(36).substr(2, 5)}`;

      // Initialize progress state
      setProcessingMap((prev) => ({
        ...prev,
        [trackId]: { progress: 5, stage: 'reading', message: 'Analyzing file format...' },
      }));

      try {
        const result = await convertAudioToWav(file, bitDepth, (status) => {
          setProcessingMap((prev) => ({
            ...prev,
            [trackId]: {
              progress: status.progress,
              stage: status.stage,
              message: status.message,
            },
          }));
        });

        const newTrackItem = {
          id: trackId,
          originalFile: file,
          processedFile: result.processedFile,
          filename: result.filename,
          processedFilename: result.processedFilename,
          duration: result.duration,
          durationFormatted: result.durationFormatted,
          sampleRate: result.sampleRate,
          bitDepth: result.bitDepth,
          channels: result.channels,
          size: result.size,
          sizeFormatted: result.sizeFormatted,
          originalFormat: result.originalFormat,
          status: 'ready',
        };

        if (isSingle) {
          onUpdateAudioTracks([newTrackItem]);
        } else {
          onUpdateAudioTracks((prev) => [...prev, newTrackItem]);
        }

        setProcessingMap((prev) => {
          const next = { ...prev };
          delete next[trackId];
          return next;
        });
      } catch (err) {
        setProcessingMap((prev) => ({
          ...prev,
          [trackId]: {
            progress: 0,
            stage: 'error',
            error: err.message || 'Audio conversion failed.',
          },
        }));
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleRemoveTrack = (trackId) => {
    onUpdateAudioTracks((prev) => prev.filter((t) => t.id !== trackId));
  };

  const handleMoveTrack = (index, direction) => {
    const newTracks = [...audioTracks];
    const targetIdx = direction === 'up' ? index - 1 : index + 1;
    if (targetIdx < 0 || targetIdx >= newTracks.length) return;
    const temp = newTracks[index];
    newTracks[index] = newTracks[targetIdx];
    newTracks[targetIdx] = temp;
    onUpdateAudioTracks(newTracks);
  };

  const trackCountValidation = validateTrackCount(releaseType, audioTracks.length);
  const canContinue = trackCountValidation.isValid && Object.keys(processingMap).length === 0;

  const handleContinueClick = () => {
    if (!trackCountValidation.isValid) return;
    onContinue();
  };

  return (
    <div className="dist-card">
      <div className="dist-card-header">
        <div>
          <h2 className="dist-card-title">
            <FileAudio size={22} color="#d9fb06" />
            {isSingle ? 'Upload Single Audio Track' : `Upload Audio Tracks for ${releaseType.toUpperCase()}`}
          </h2>
          <p className="dist-card-desc">
            {isSingle
              ? 'Single releases must contain exactly 1 track.'
              : releaseType === 'ep'
              ? 'Upload at least 2 audio files (EP releases must contain at least 2 tracks). Each file generates a track details form.'
              : 'Upload at least 7 audio files (Album releases must contain at least 7 tracks). Each file generates a track details form.'}
          </p>
        </div>

        {/* PCM Bit Depth Selector */}
        <div className="dist-bitdepth-selector" style={{ margin: 0 }}>
          <span className="dist-bitdepth-label">
            <Sliders size={16} color="#d9fb06" /> Output Bit Depth:
          </span>
          <div className="dist-segmented-control" role="group" aria-label="PCM Bit Depth">
            <button
              type="button"
              className={`dist-segment-btn ${bitDepth === 16 ? 'active' : ''}`}
              onClick={() => onChangeBitDepth(16)}
            >
              16-bit (Standard)
            </button>
            <button
              type="button"
              className={`dist-segment-btn ${bitDepth === 24 ? 'active' : ''}`}
              onClick={() => onChangeBitDepth(24)}
            >
              24-bit (Hi-Res)
            </button>
          </div>
        </div>
      </div>

      {/* Upload Dropzone */}
      {(!isSingle || audioTracks.length === 0) && (
        <div
          className={`dist-upload-dropzone ${isDragging ? 'drag-active' : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={SUPPORTED_AUDIO_EXTENSIONS.join(',')}
            multiple={!isSingle}
            style={{ display: 'none' }}
            onChange={(e) => {
              if (e.target.files) handleFiles(e.target.files);
              e.target.value = '';
            }}
          />

          <div className="dist-upload-icon-circle">
            <UploadCloud size={28} />
          </div>

          <h3 className="dist-upload-heading">
            {isSingle ? 'Choose or Drag 1 Audio File' : `Choose or Drag Audio Files (${releaseType.toUpperCase()})`}
          </h3>
        </div>
      )}

      {/* Active Processing Tasks */}
      {Object.entries(processingMap).map(([id, item]) => (
        <div key={id} className={`dist-audio-track-item ${item.stage === 'error' ? 'error' : ''}`}>
          <div className="dist-track-top-row">
            <div className="dist-track-name-badge">
              <RefreshCw size={18} color="#d9fb06" className="animate-spin" />
              <span className="dist-track-filename">Processing Audio Stream...</span>
            </div>
            <span style={{ fontSize: '0.8rem', color: '#d9fb06', fontWeight: 600 }}>
              {item.progress}%
            </span>
          </div>

          {item.stage === 'error' ? (
            <div style={{ color: '#ff6b6b', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <AlertCircle size={16} /> {item.error || 'Audio conversion failed.'}
            </div>
          ) : (
            <>
              <div className="dist-progress-bar-container">
                <div className="dist-progress-bar-fill" style={{ width: `${item.progress}%` }} />
              </div>
              <div className="dist-progress-msg">
                <span>{item.message}</span>
                <span>44.1 kHz • {bitDepth}-bit WAV</span>
              </div>
            </>
          )}
        </div>
      ))}

      {/* Successfully Processed Audio Tracks List */}
      {audioTracks.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.8rem' }}>
            <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#ffffff', margin: 0 }}>
              Ready Audio Tracks ({audioTracks.length})
            </h4>
            {!isSingle && (
              <button
                type="button"
                className="dist-add-btn"
                onClick={() => fileInputRef.current?.click()}
                style={{ margin: 0 }}
              >
                + Add More Audio Files
              </button>
            )}
          </div>

          {audioTracks.map((track, idx) => (
            <div key={track.id} className="dist-audio-track-item done">
              <div className="dist-track-top-row">
                <div className="dist-track-name-badge">
                  <span className="dist-track-num-pill">Track {idx + 1}</span>
                  <span className="dist-track-filename">{track.filename}</span>
                  <CheckCircle2 size={18} color="#d9fb06" />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  {!isSingle && (
                    <>
                      <button
                        type="button"
                        onClick={() => handleMoveTrack(idx, 'up')}
                        disabled={idx === 0}
                        title="Move Up"
                        style={{
                          background: 'rgba(255,255,255,0.08)',
                          border: 'none',
                          color: idx === 0 ? 'rgba(255,255,255,0.2)' : '#fff',
                          borderRadius: '6px',
                          padding: '0.3rem',
                          cursor: idx === 0 ? 'default' : 'pointer',
                        }}
                      >
                        <MoveUp size={15} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveTrack(idx, 'down')}
                        disabled={idx === audioTracks.length - 1}
                        title="Move Down"
                        style={{
                          background: 'rgba(255,255,255,0.08)',
                          border: 'none',
                          color: idx === audioTracks.length - 1 ? 'rgba(255,255,255,0.2)' : '#fff',
                          borderRadius: '6px',
                          padding: '0.3rem',
                          cursor: idx === audioTracks.length - 1 ? 'default' : 'pointer',
                        }}
                      >
                        <MoveDown size={15} />
                      </button>
                    </>
                  )}

                  <button
                    type="button"
                    onClick={() => handleRemoveTrack(track.id)}
                    className="dist-remove-btn"
                    title="Remove Track"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>

              {/* Technical Audio Metadata Chips */}
              <div className="dist-track-tech-chips">
                <span className="dist-tech-chip accent">WAV PCM</span>
                <span className="dist-tech-chip">44.1 kHz</span>
                <span className="dist-tech-chip">{track.bitDepth}-bit</span>
                <span className="dist-tech-chip">{track.channels === 2 ? 'Stereo' : 'Mono'}</span>
                <span className="dist-tech-chip">⏱ {track.durationFormatted}</span>
                <span className="dist-tech-chip">📦 {track.sizeFormatted}</span>
                <span className="dist-tech-chip">Source: {track.originalFormat}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Track Count Validation Error Banner */}
      {audioTracks.length > 0 && !trackCountValidation.isValid && (
        <div
          style={{
            background: 'rgba(255, 80, 80, 0.12)',
            border: '1px solid rgba(255, 80, 80, 0.35)',
            borderRadius: '10px',
            padding: '0.9rem 1.2rem',
            marginTop: '1.2rem',
            color: '#ff8585',
            fontSize: '0.88rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.6rem',
          }}
        >
          <AlertCircle size={18} />
          <span>{trackCountValidation.error}</span>
        </div>
      )}

      {/* Footer Navigation Buttons */}
      <div className="dist-footer-actions">
        <button type="button" className="dist-btn-secondary" onClick={onBack}>
          <ArrowLeft size={18} /> Back
        </button>

        <button
          type="button"
          className="dist-btn-primary"
          onClick={handleContinueClick}
          disabled={!canContinue}
        >
          Continue to Track Details ({audioTracks.length} {audioTracks.length === 1 ? 'Track' : 'Tracks'}) <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
}
